import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GuideTour } from "../../GuideTour/GuideTour";
import { useGuideTour } from "../../GuideTour/useGuideTour";
import { AddFundsHelpShell, type AddFundsHelpShellRef } from "./AddFundsHelpShell";
import { WL_ADD_FUNDS_HELP_STEPS } from "./addFundsHelpSteps";
import { WL_ADD_FUNDS_HELP_TOUR_STORAGE_KEY } from "../whitelabelTourStorage";

function waitMs(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const t = window.setTimeout(() => {
      if (signal.aborted) reject(new DOMException("Aborted", "AbortError"));
      else resolve();
    }, ms);
    signal.addEventListener(
      "abort",
      () => {
        window.clearTimeout(t);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true }
    );
  });
}

const HELP = {
  spotlight: 1100,
  branch: 900,
  transition: 600,
} as const;

const STEPS = WL_ADD_FUNDS_HELP_STEPS;

type TourApi = NonNullable<ReturnType<typeof useGuideTour>>;

async function runAddFundsSegment(
  stepIndex: number,
  signal: AbortSignal,
  sh: AddFundsHelpShellRef,
  T: TourApi,
  navigate: (path: string, opts?: { replace?: boolean }) => void,
  totalSteps: number
): Promise<void> {
  const step = STEPS[stepIndex];
  if (!step) return;

  sh.prepareForTourStep(stepIndex);
  await waitMs(220, signal);

  T.jumpToStep(stepIndex);
  await waitMs(HELP.transition, signal);

  await waitMs(HELP.spotlight, signal);
  await sh.pulseControl(step.selector);
  await waitMs(HELP.branch, signal);

  if (stepIndex === totalSteps - 1) {
    T.complete();
    navigate("/whitelabel", { replace: true });
  }
}

export default function AddFundsHelpTourPage() {
  const navigate = useNavigate();
  const [helpOrchestratorGeneration, setHelpOrchestratorGeneration] = useState(0);
  const shellRef = useRef<AddFundsHelpShellRef | null>(null);
  const tourRef = useRef<ReturnType<typeof useGuideTour> | null>(null);
  const orchestratorAbortRef = useRef<AbortController | null>(null);
  const resumeFromStepRef = useRef<number | null>(null);

  const handleHelpExit = useCallback(() => {
    tourRef.current?.skip();
    navigate("/whitelabel", { replace: true });
  }, [navigate]);

  const tour = useGuideTour({
    steps: STEPS,
    storageKey: WL_ADD_FUNDS_HELP_TOUR_STORAGE_KEY,
    flowStepIndex: 0,
    persistCompletion: false,
    onEscape: handleHelpExit,
  });
  tourRef.current = tour;

  useEffect(() => {
    tour.start();
    return () => {
      tour.stop();
    };
  }, [tour.start, tour.stop]);

  useEffect(() => {
    if (!tour.isActive) return;

    const ac = new AbortController();
    orchestratorAbortRef.current = ac;
    const signal = ac.signal;

    const run = async () => {
      const waitForShell = async () => {
        for (let i = 0; i < 40; i += 1) {
          if (signal.aborted) throw new DOMException("Aborted", "AbortError");
          if (shellRef.current) return shellRef.current;
          await waitMs(50, signal);
        }
        return shellRef.current;
      };

      try {
        await waitMs(0, signal);
        const sh = await waitForShell();
        if (!sh) return;

        const from = resumeFromStepRef.current ?? 0;
        resumeFromStepRef.current = null;

        const T = tourRef.current;
        if (!T) return;

        for (let s = from; s < STEPS.length; s += 1) {
          if (signal.aborted) throw new DOMException("Aborted", "AbortError");
          await runAddFundsSegment(s, signal, sh, T, navigate, STEPS.length);
          if (!tourRef.current?.isActive) return;
        }
      } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") return;
        console.error(e);
      }
    };

    void run();
    return () => {
      ac.abort();
      orchestratorAbortRef.current = null;
    };
  }, [helpOrchestratorGeneration, navigate, tour.isActive]);

  const handleHelpTourPrev = useCallback(() => {
    const t = tourRef.current;
    if (!t || t.stepIndex <= 0) return;
    resumeFromStepRef.current = Math.max(0, t.stepIndex - 1);
    orchestratorAbortRef.current?.abort();
    t.goPrev();
    setHelpOrchestratorGeneration((g) => g + 1);
  }, []);

  const helpAutopilotRunning = tour.isActive;

  return (
    <>
      <AddFundsHelpShell ref={shellRef} blockPointerForAutopilot={helpAutopilotRunning} />
      <GuideTour
        isActive={tour.isActive}
        targetRect={tour.targetRect}
        stepIndex={tour.stepIndex}
        totalSteps={tour.totalSteps}
        content={tour.activeStep?.content ?? ""}
        demoKind={tour.activeStep?.demoKind}
        spotlightPadding={tour.activeStep?.spotlightPadding ?? 8}
        autopilotMode={helpAutopilotRunning}
        autopilotShowPrevious={helpAutopilotRunning}
        onNext={tour.goNext}
        onPrev={handleHelpTourPrev}
        onSkip={handleHelpExit}
      />
    </>
  );
}
